import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Users, Euro, ImageIcon, ArrowRight } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type Venue = Tables<"venues"> & {
  avg_rating?: number | null;
  review_count?: number | null;
  estimated_price?: number | null;
  main_image?: string | null;
  reference_price?: number | null;
  has_multiple_rates?: boolean;
};

export function VenueCard({ venue }: { venue: Venue }) {
  const typeLabels: Record<string, string> = {
    sala: "Sala",
    teatro: "Teatro",
    auditorio: "Auditorio",
    exterior: "Exterior",
  };

  const refPrice = venue.reference_price ?? venue.official_price;
  const hasMultiple = venue.has_multiple_rates;
  const communityPrice = venue.estimated_price;

  return (
    <Link to={`/sala/${venue.slug}`}>
      <Card className="group h-full overflow-hidden transition-all hover:shadow-md hover:border-primary/20">
        <CardContent className="p-0 flex flex-col h-full">
          {/* Image */}
          <div className="relative aspect-video w-full overflow-hidden bg-secondary">
            {venue.main_image ? (
              <img
                src={venue.main_image}
                alt={venue.name}
                className="absolute inset-0 h-full w-full object-cover object-center"
                loading="lazy"
                width={600}
                height={400}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground/40">
                <ImageIcon className="h-8 w-8" />
                <span className="text-xs font-medium">Sin foto</span>
              </div>
            )}
            {venue.type && (
              <Badge className="absolute top-3 right-3 bg-background/90 text-foreground text-xs backdrop-blur-sm">
                {typeLabels[venue.type] || venue.type}
              </Badge>
            )}
          </div>

          {/* Info */}
          <div className="flex flex-1 flex-col p-4">
            <h3 className="font-display text-base font-semibold leading-tight mb-2 group-hover:text-primary transition-colors">
              {venue.name}
            </h3>

            <div className="flex flex-col gap-1.5 text-sm text-muted-foreground mb-4">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{venue.city}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 shrink-0" />
                <span>{venue.capacity ? `${venue.capacity} personas` : "Aforo no disponible"}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Euro className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {refPrice != null
                    ? `${hasMultiple ? "Desde " : "Precio Oficial: "}${Math.round(refPrice)}€`
                    : "Precio oficial no disponible"}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Euro className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {communityPrice != null
                    ? `Precio Comunidad: ~${Math.round(communityPrice)}€`
                    : "Precio comunidad no disponible"}
                </span>
              </div>
            </div>

            {/* Ver Sala button */}
            <div className="mt-auto">
              <div className="flex items-center justify-between rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors group-hover:bg-primary/90">
                <span>Ver Sala</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
